import type { BinaryLabel, Candle } from "../types";

/**
 * How far price travelled each way before a trend Round resolved.
 *
 * The reveal used to report only the winning barrier and how long it took,
 * which tells a player what happened but nothing about their decision. The
 * path either went their way first and gave back, or never went their way at
 * all, and those two are different lessons. This module measures that, and
 * nothing else: no copy, no verdict, no advice.
 *
 * Measured over the Forward candles the walk actually looked at, entry to the
 * resolving candle inclusive, using each candle's high for upward travel and
 * its low for downward travel, exactly as `labeling.ts` walks the barriers.
 */

export type Excursion = {
  /**
   * The furthest price got in the guessed direction, as a percent of entry,
   * signed the way the chart reads it: positive for a Higher guess, negative
   * for a Lower one, so it lines up with the barrier labels. Zero when price
   * never moved that way at all.
   */
  towardPct: number;
  /** The same for the other direction: the furthest price went against the guess. */
  againstPct: number;
  /**
   * `towardPct` as a fraction (0..1) of the distance from entry to the barrier
   * the player predicted. 1 means the barrier was reached. Clamped, so an
   * overshooting candle still reads as 1 rather than more than the whole way.
   */
  towardFraction: number;
  /** The same against: a fraction of the distance to the barrier they did not predict. */
  againstFraction: number;
};

export type ExcursionInput = {
  /** The last Context candle's close, what the barriers were struck from. */
  entry: number;
  /** The Round's Forward path, unsliced. */
  forward: Candle[];
  /**
   * The resolution's `daysToResolve`, which is 1-based (see `walk` in
   * `labeling.ts`): the count of candles walked, so the resolving candle is
   * `forward[daysToResolve - 1]` and is included in the measurement.
   */
  daysToResolve: number;
  guess: BinaryLabel;
  upperBarrier: number;
  lowerBarrier: number;
};

const ZERO: Excursion = {
  towardPct: 0,
  againstPct: 0,
  towardFraction: 0,
  againstFraction: 0,
};

/** A share of the way to a barrier, kept inside 0..1 and safe on a zero-width barrier. */
function fractionOf(move: number, distance: number): number {
  if (distance <= 0) return 0;
  return Math.min(move / distance, 1);
}

export function excursion({
  entry,
  forward,
  daysToResolve,
  guess,
  upperBarrier,
  lowerBarrier,
}: ExcursionInput): Excursion {
  const walked = forward.slice(0, Math.max(daysToResolve, 0));
  if (walked.length === 0 || entry === 0) return ZERO;

  const highest = Math.max(...walked.map((candle) => candle.high));
  const lowest = Math.min(...walked.map((candle) => candle.low));

  // Travel is measured from entry and floored at zero: a path that only ever
  // fell never "went up by a negative amount", it simply never went up.
  const up = Math.max(highest - entry, 0);
  const down = Math.max(entry - lowest, 0);

  const towardMove = guess === "up" ? up : down;
  const againstMove = guess === "up" ? down : up;
  const towardDistance = guess === "up" ? upperBarrier - entry : entry - lowerBarrier;
  const againstDistance = guess === "up" ? entry - lowerBarrier : upperBarrier - entry;

  const sign = guess === "up" ? 1 : -1;
  return {
    towardPct: (sign * towardMove * 100) / entry,
    againstPct: (-sign * againstMove * 100) / entry,
    towardFraction: fractionOf(towardMove, towardDistance),
    againstFraction: fractionOf(againstMove, againstDistance),
  };
}
